import React from "react";
import Notif from "watnotif/dist/js/watnotif";
import notificationTemplate, {
  NotificationObject,
  errorTemplate,
} from "./notification.html";

import(
  /* webpackChunkName: "notifications-standalone" */ "~/less/notifications-standalone.less"
);

/** Wrapper for watnotif. Adds a custom notification template and a fader. */
export default class Notification {
  private static get notificationFader() {
    const element = document.querySelector(".notification-fader");
    if (!element)
      throw new Error(
        "An element with the class name .notification-fader is required!",
      );
    return element;
  }

  private static getBase = Notif.prototype.getBase;

  private static getWrapper = Notif.prototype.getWrapper;

  private static setup = false;

  /**
   * Create a notification.
   * @param options Object properties are interpolated into the error template.
   * @param options.heading Text for the header.
   * @param options.href Where the primary button links to. The image will also be linked
   * to this href if an image is included.
   * @param options.imageUrl Optional image url.
   * @param options.body Optional text for the body.
   * @param options.buttonText Text of the primary button.
   * @param options.secondButtonText Text of the secondary button.
   * @param options.secondHref Href of secondary button.
   * @param type Add error styling by passing 'Error' for this parameter.
   */
  public constructor(
    options: NotificationObject,
    type = "default",
    eventInfo?: React.SyntheticEvent,
    display = 10000,
  ) {
    if (!Notification.setup) {
      Notification.setupNotif(eventInfo);
      Notification.setup = true;
    }

    return Notification.create(options, type, display);
  }

  public static create(
    options: NotificationObject,
    type: string,
    display: number,
  ) {
    if (type === "error") {
      const errorHtml = errorTemplate(options);
      return new Notif(errorHtml, type).display(display);
    }
    const html = notificationTemplate(options);
    return new Notif(html, type).display(display);
  }

  public static setupNotif(eventInfo?: React.SyntheticEvent) {
    Notification.notificationFader.addEventListener("click", () =>
      Notification.closeAll(eventInfo),
    );

    document.addEventListener("keydown", (evt) => {
      const notificationItems = [
        ...Notification.getWrapper().children,
      ] as HTMLElement[];
      if (notificationItems.length !== 0) {
        const firstNotification = notificationItems[0];
        if (evt.key === "Escape") {
          firstNotification?.remove();
          // remove the darkened background provided for notifications, if none are left
          if (notificationItems.length - 1 === 0) {
            Notification.closeFader(eventInfo);
          }
        }
      }
    });

    const { insertBefore, removeChild } = Object.getPrototypeOf(
      Notification.getWrapper(),
    );

    Notif.prototype.getBase = function getBasePatched(
      classes: string,
      msg: string,
    ) {
      const notificationElement = Notification.getBase(
        `notification notification--${classes}`,
        msg,
      );

      const close = notificationElement.querySelector(".close-notif");
      if (close) close.remove();

      return notificationElement;
    };

    Notification.getWrapper().insertBefore = (...insertBeforeArgs) => {
      Notification.activateFade();
      return insertBefore.apply(Notification.getWrapper(), insertBeforeArgs);
    };

    Notification.getWrapper().removeChild = (...removeChildArgs) => {
      if (Notification.getWrapper().children.length < 2)
        Notification.closeFader(eventInfo);
      return removeChild.apply(Notification.getWrapper(), removeChildArgs);
    };
  }

  private static activateFade() {
    Notification.notificationFader.classList.add("open");
  }

  private static closeFader(eventInfo?: React.SyntheticEvent) {
    Notification.notificationFader.classList.remove("open");
    if (eventInfo) {
      const lastFocusedElement = eventInfo.target;

      (lastFocusedElement as HTMLElement).focus();
    }
  }

  public static closeAll(eventInfo?: React.SyntheticEvent) {
    Notification.closeFader(eventInfo);

    ([...Notification.getWrapper().children] as HTMLElement[]).forEach(
      (element) => element.click(),
    );
  }
}
